/**
 * Loads a third-party library into the block editor on first use.
 *
 * WordPress enqueues the `script` and `style` handles of every registered block on every
 * editor screen - on-demand loading only covers the front end. With 28 blocks that pulled
 * Swiper and Leaflet into every post edit screen, whether or not the post had a slider or
 * a map in it. inc/config/assets.php drops them there and passes their URLs instead, as
 * `window.bnEditorAssets` - see bn_dequeue_front_only_block_assets().
 *
 * The front end never goes through here: there the libraries stay ordinary block.json
 * dependencies, enqueued when the block renders.
 *
 * Lives outside src/js/ on purpose - webpack turns every file directly in src/js/ into its
 * own bundle, and this is a module, not an entry point.
 */

const requests = new Map();

/**
 * Appends one asset to the current document, at most once.
 *
 * The current document matters: block previews render inside the editor canvas iframe, so
 * a library appended to the outer document would be invisible to them.
 *
 * @param {string} url Asset URL.
 * @return {Promise} Resolves once the asset has loaded.
 */
const injectAsset = ( url ) => {
	if ( requests.has( url ) ) {
		return requests.get( url );
	}

	const isStyle = url.split( '?' )[ 0 ].endsWith( '.css' );
	const element = document.createElement( isStyle ? 'link' : 'script' );

	if ( isStyle ) {
		element.rel = 'stylesheet';
		element.href = url;
	} else {
		element.src = url;
	}

	const request = new Promise( ( resolve, reject ) => {
		element.onload = resolve;
		element.onerror = () => reject( new Error( `bn: could not load ${ url }` ) );
	} );

	requests.set( url, request );
	document.head.appendChild( element );

	return request;
};

/**
 * Makes a library available before a block preview initialises.
 *
 * An unknown name resolves straight away, so a block still works if the library turns out
 * to be enqueued the usual way after all.
 *
 * @param {string} name Library name, as keyed in bn_get_lazy_editor_assets().
 * @return {Promise} Resolves once the library is ready.
 */
export const loadEditorAsset = ( name ) => {
	const urls = ( window.bnEditorAssets || {} )[ name ];

	if ( ! urls ) {
		return Promise.resolve();
	}

	return Promise.all( urls.map( injectAsset ) );
};
